import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, Subject, takeUntil } from 'rxjs';
import { CoachCalendarService } from '../../../core/services/coach-calendar.service';
import { CoachRescheduleService } from '../../../core/services/coach-reschedule.service';
import { IRescheduleRequest } from '../../../core/models/appointment.model';

const PAGE_SIZE = 10;

/**
 * Every request this coach has ever received, resolved ones included — the agenda card itself only
 * carries what is still waiting, so this is where a coach checks what they answered and when.
 *
 * Pending rows stay actionable here; answering one refreshes this list and, through the service,
 * the agenda behind it.
 */
@Component({
  selector: 'coach-reschedule-requests-modal',
  templateUrl: './reschedule-requests-modal.component.html',
  styleUrl: './reschedule-requests-modal.component.scss',
})
export class RescheduleRequestsModalComponent implements OnInit, OnDestroy {
  requests: IRescheduleRequest[] = [];

  page = 1;
  lastPage = 1;

  isLoading = false;
  busyRequestId?: number;

  destroyed$ = new Subject();

  constructor(
    public modal: NgbActiveModal,
    private calendarService: CoachCalendarService,
    private rescheduleService: CoachRescheduleService,
  ) {
  }

  ngOnInit(): void {
    this.getRequests();
  }

  hasMore(): boolean {
    return this.page < this.lastPage;
  }

  getRequests(append = false) {
    this.isLoading = true;
    this.calendarService.getRescheduleRequests(`?page=${this.page}&size=${PAGE_SIZE}`)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(r => {
        const result = (r.data?.result ?? []) as IRescheduleRequest[];
        this.requests = append ? [...this.requests, ...result] : result;
        this.lastPage = r.data?.meta?.lastPage ?? 1;
      });
  }

  loadMore() {
    if (this.isLoading || !this.hasMore()) {
      return;
    }

    this.page += 1;
    this.getRequests(true);
  }

  approve(request: IRescheduleRequest) {
    this.busyRequestId = request.id;
    this.rescheduleService.approve(request)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.busyRequestId = undefined))
      .subscribe(isDone => isDone && this.reload());
  }

  reject(request: IRescheduleRequest) {
    this.busyRequestId = request.id;
    this.rescheduleService.reject(request)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.busyRequestId = undefined))
      .subscribe(isDone => isDone && this.reload());
  }

  private reload() {
    this.page = 1;
    this.getRequests();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
